import { Scene } from "@babylonjs/core/scene";
import { Engine } from "@babylonjs/core/Engines/engine";
import { ListenableEvent, Log } from "../utils";
import { isNullOrEmpty } from "../utils/string";
import { AppDocument } from "./AppDocument";
import { AppSettings } from "./AppSettings";
import { CommandManager } from "./cmd/CommandManager";
import { I18nManager } from "./I18nManager";
import { ModelManager } from "./mdl/ModelManager";
import { RenderingManager } from "./rendering/RenderingManager";
import { ToolManager } from "./tool/ToolManager";
import { Viewer } from "./view/Viewer";
export class Application {
    get version() {
        //will be replaced during build process
        return ("8.4.0");
    }
    get buildInfo() {
        //will be replaced during build process
        return ("0faf1581 master 2023-03-14");
    }
    get engine() {
        return (this.mEngine);
    }
    get scene() {
        return (this.mScene);
    }
    get i18n() {
        return (this.mI18n);
    }
    get settings() {
        return (this.mSettings);
    }
    get document() {
        return (this.mDocument);
    }
    get viewer() {
        return (this.mViewer);
    }
    get model() {
        return (this.mModelManager);
    }
    get commands() {
        return (this.mCommandManager);
    }
    get tools() {
        return (this.mToolManager);
    }
    get rendering() {
        return (this.mRenderingManager);
    }
    get theme() {
        return (this.mTheme);
    }
    /**
     * Callbacks which should be implemented by the host application.
     */
    get appCallbacks() {
        return (this.mAppCallbacks);
    }
    /**
     * The list of registered modules.
     */
    get modules() {
        return (this.mModulesArray);
    }
    /**
     * The options used to initialize the application.
     * If the aplication was not initialized yet these options represent the default options.
     */
    get initOptions() {
        return (this.mInitOptions);
    }
    constructor() {
        /**
         * Will be triggered periodically from the applications main loop. Can be used for custom animations.
         */
        this.eventAnimate = new ListenableEvent(this);
        //default theme
        this.mTheme = {
            mainColorBright: "#95C11F",
            mainColorDark: "#7da50b",
            fontFamily: "'Open Sans', Tahoma, 'Geneva', sans-serif"
        };
        //default init options
        this.mInitOptions = {
            hardwareAntialiasing: false,
            preserveDrawingBuffer: true,
            stencilBuffer: false,
            adaptToDeviceRatio: true,
            maximumDeviceRatio: 2.0,
            autoResizeViewer: true,
            audioEngine: false,
            disableWebGL2Support: true
        };
        this.mAppCallbacks = {};
        this.mModules = new Map();
        this.mModulesArray = new Array();
        this.applicationName = "";
        this.applicationVersion = "";
        this.mI18n = new I18nManager();
        this.mSettings = new AppSettings();
        this.mToolManager = new ToolManager();
        this.mCommandManager = new CommandManager();
        this.mDocument = new AppDocument(this);
        this.mRenderingManager = new RenderingManager(this);
    }
    /**
     * Has to be called once to intialize the application.
     * The 3d rendering canvas will be created as a children of pViewerContainer.
     * Returns false if initialization failed.
     */
    initialize(pViewerContainer, pOptions) {
        if (pViewerContainer == null)
            return (false);
        if (pOptions != null) {
            for (const tKey in pOptions) {
                const tValue = pOptions[tKey];
                if (tValue !== undefined) {
                    this.mInitOptions[tKey] = tValue;
                }
            }
        }
        //create viewer which creates the canvas
        this.mViewer = new Viewer(this, pViewerContainer);
        //create BABYLON Engine which intializes the GL context
        this.mEngine = new Engine(this.mViewer.canvas, this.mInitOptions.hardwareAntialiasing, {
            antialias: this.mInitOptions.hardwareAntialiasing,
            preserveDrawingBuffer: this.mInitOptions.preserveDrawingBuffer,
            limitDeviceRatio: this.mInitOptions.maximumDeviceRatio,
            stencil: this.mInitOptions.stencilBuffer,
            audioEngine: this.mInitOptions.audioEngine,
            autoEnableWebVR: false,
            xrCompatible: false,
            disableWebGL2Support: this.mInitOptions.disableWebGL2Support
        }, this.mInitOptions.adaptToDeviceRatio);
        this.mEngine.enableOfflineSupport = false; //disable manifest check e.g. during obj loading
        /*
            * disable parallel shader compilation (only supported from Chrome currently)
            * If shaders are compiled parallel, they are rendered asynchonously.
            * In combination with our on-demand rendering this can lead to
            * missing geometries in some rendered frames (e.g during screenshot generation).
            * TODO: We could support this by listening to the onCompiled event of all materials but
            * the benefit of parallel shader compilation does not seem to be very high in our case.
            */
        if (this.mEngine.getCaps().parallelShaderCompile != null) {
            this.mEngine.getCaps().parallelShaderCompile = undefined;
            Log.info("Disabled parallel shader compilation.");
        }
        //create BABYLON scene
        this.mScene = new Scene(this.mEngine);
        this.mModelManager = new ModelManager(this);
        //initialize the viewer
        if (!this.mViewer.initialize(this.mInitOptions.autoResizeViewer))
            return (false);
        //start main loop
        this.mEngine.runRenderLoop(this.processMainLoop.bind(this));
        return (true);
    }
    /** Dispose application and free used resources. */
    dispose() {
        //not initialized or already disposed?
        if (this.mEngine == null || this.mScene.isDisposed)
            return;
        this.mViewer.dispose();
        this.mEngine.dispose();
    }
    /** Registers a new application module. */
    registerModule(pModule) {
        if (pModule != null && !this.mModules.has(pModule.moduleId)) {
            this.mModules.set(pModule.moduleId, pModule);
            this.mModulesArray.push(pModule);
        }
    }
    /** Returns the module with the given id or null if the module was not found. */
    getModule(pModuleId) {
        if (isNullOrEmpty(pModuleId))
            return (null);
        const tModule = this.mModules.get(pModuleId);
        return (tModule !== undefined ? tModule : null);
    }
    processMainLoop() {
        this.mToolManager._updateActiveToolState();
        this.eventAnimate.trigger();
        this.mViewer._renderFrame(false);
    }
}
